import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarRange, FileText, Users, Handshake, Package, ReceiptText,
  FileMinus2, FilePlus2, IndianRupee, WalletCards, ArrowDownLeft, ArrowUpRight, BookOpen,
  Landmark, BarChart3, Settings, UsersRound, ScrollText, Search, Bell, Menu, X, LogOut,
  KeyRound, Plus, Feather, ChevronDown, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { can, ROLE_LABELS, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const NAV = [
  { group: null, items: [
    { label: "Dashboard", to: "/", icon: LayoutDashboard, module: "dashboard", id: "dashboard" },
  ]},
  { group: "OPERATIONS", items: [
    { label: "Bookings", to: "/bookings", icon: CalendarRange, module: "bookings", id: "bookings" },
    { label: "Quotations", to: "/quotations", icon: FileText, module: "quotations", id: "quotations" },
    { label: "Customers", to: "/customers", icon: Users, module: "customers", id: "customers" },
    { label: "B2B Agents", to: "/agents", icon: Handshake, module: "agents", id: "agents" },
    { label: "Services & Packages", to: "/services", icon: Package, module: "services", id: "services" },
  ]},
  { group: "BILLING", items: [
    { label: "Invoices", to: "/invoices", icon: ReceiptText, module: "invoices", id: "invoices" },
    { label: "Credit Notes", to: "/credit-notes", icon: FileMinus2, module: "notes", id: "credit-notes" },
    { label: "Debit Notes", to: "/debit-notes", icon: FilePlus2, module: "notes", id: "debit-notes" },
    { label: "Payments", to: "/payments", icon: IndianRupee, module: "payments", id: "payments" },
  ]},
  { group: "ACCOUNTING", items: [
    { label: "Expenses & Purchases", to: "/expenses", icon: WalletCards, module: "expenses", id: "expenses" },
    { label: "Receivables", to: "/receivables", icon: ArrowDownLeft, module: "reports", id: "receivables" },
    { label: "Payables", to: "/payables", icon: ArrowUpRight, module: "reports", id: "payables" },
    { label: "Ledgers", to: "/ledgers", icon: BookOpen, module: "ledgers", id: "ledgers" },
  ]},
  { group: "GST", items: [
    { label: "GST Center", to: "/gst", icon: Landmark, module: "gst", id: "gst" },
  ]},
  { group: "REPORTS", items: [
    { label: "Reports Center", to: "/reports", icon: BarChart3, module: "reports", id: "reports" },
  ]},
  { group: "ADMIN", items: [
    { label: "Users & Roles", to: "/users", icon: UsersRound, module: "users", id: "users" },
    { label: "Settings", to: "/settings", icon: Settings, module: "settings", id: "settings" },
    { label: "Audit Log", to: "/audit", icon: ScrollText, module: "audit", id: "audit" },
  ]},
];

function SidebarContent({ onNavigate }) {
  const { user } = useAuth();
  const location = useLocation();
  return (
    <div className="flex flex-col h-full bg-[#092B28] text-white">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-[#136A63]/40">
        <div className="h-10 w-10 rounded-full bg-[#C9A227] flex items-center justify-center shrink-0">
          <Feather className="h-5 w-5 text-[#092B28]" />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-lg leading-tight tracking-wide text-white">DREAMZZ</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A227]">Travels ERP</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        {NAV.map((section, si) => {
          const visible = section.items.filter((i) => can(user?.role, i.module));
          if (!visible.length) return null;
          return (
            <div key={si}>
              {section.group && (
                <div className="px-3 mb-1.5 text-[10px] font-bold tracking-[0.18em] text-[#6E8F8B]">{section.group}</div>
              )}
              <div className="space-y-0.5">
                {visible.map((item) => {
                  const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
                  return (
                    <NavLink key={item.to} to={item.to} onClick={onNavigate}
                      data-testid={`nav-item-${item.id}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150",
                        active
                          ? "bg-[#136A63] text-[#D4AF37] font-semibold border-l-4 border-[#C9A227] shadow-sm"
                          : "text-[#A3B8B5] hover:bg-[#136A63]/50 hover:text-white border-l-4 border-transparent"
                      )}>
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="px-5 py-3 border-t border-[#136A63]/40 text-[10px] text-[#6E8F8B]">
        Chikkamagaluru, Karnataka · GST-ready
      </div>
    </div>
  );
}

function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    if (q.length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(r.data.results);
      } catch { /* ignore */ }
      setLoading(false);
    }, 250);
  }, [q, open]);

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-search-trigger"
        className="hidden sm:flex items-center gap-2 w-64 lg:w-80 px-3 py-1.5 rounded-lg border border-[#E5E0D8] bg-[#FAF8F5] text-sm text-[#5C6B69] hover:border-[#C9A227]/60 transition-colors">
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search invoices, customers…</span>
        <kbd className="text-[10px] bg-white border border-[#E5E0D8] rounded px-1.5 py-0.5">Ctrl K</kbd>
      </button>
      <button onClick={() => setOpen(true)} className="sm:hidden p-2" data-testid="global-search-trigger-mobile">
        <Search className="h-5 w-5 text-[#5C6B69]" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl p-0 overflow-hidden" data-testid="global-search-dialog">
          <div className="flex items-center gap-2 px-4 border-b border-[#E5E0D8]">
            <Search className="h-4 w-4 text-[#5C6B69]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search invoice no, customer, phone, GSTIN, booking, agent, payment ref…"
              className="flex-1 py-3.5 bg-transparent outline-none text-sm" data-testid="global-search-input" />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-[#5C6B69]" />}
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {q.length >= 2 && !loading && results.length === 0 && (
              <p className="text-sm text-[#5C6B69] text-center py-8">No results for “{q}”</p>
            )}
            {results.map((r, i) => (
              <button key={i} onClick={() => { setOpen(false); navigate(r.link); }}
                data-testid={`search-result-${i}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#FAF8F5] text-left transition-colors">
                <span className="text-[10px] font-bold uppercase tracking-wide bg-[#0D4F4A]/10 text-[#0D4F4A] rounded px-1.5 py-0.5 w-20 text-center shrink-0">{r.type}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate">{r.label}</span>
                  {r.sub && <span className="block text-xs text-[#5C6B69] truncate">{r.sub}</span>}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/notifications").then((r) => setItems(r.data.items)).catch(() => {});
  }, []);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-lg hover:bg-[#FAF8F5] transition-colors" data-testid="notifications-bell">
          <Bell className="h-5 w-5 text-[#5C6B69]" />
          {items.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4.5 min-w-4 px-1 rounded-full bg-[#C9A227] text-[#092B28] text-[10px] font-bold flex items-center justify-center"
              data-testid="notifications-count">{items.length}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-96 overflow-y-auto" data-testid="notifications-panel">
        <DropdownMenuLabel>Reminders & Alerts</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && <div className="px-3 py-6 text-sm text-[#5C6B69] text-center">All clear. Nothing pending.</div>}
        {items.map((n, i) => (
          <DropdownMenuItem key={i} className="cursor-pointer py-2.5"
            onSelect={() => { setOpen(false); navigate(n.link); }} data-testid={`notification-${i}`}>
            <span className={cn("h-2 w-2 rounded-full shrink-0 mr-1",
              n.severity === "error" ? "bg-red-500" : n.severity === "warning" ? "bg-amber-500" : "bg-sky-500")} />
            <span className="text-xs leading-snug">{n.message}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ChangePasswordDialog({ open, onClose }) {
  const [form, setForm] = useState({ old_password: "", new_password: "" });
  const submit = async () => {
    try {
      await api.post("/auth/change-password", form);
      toast.success("Password changed");
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-display">Change Password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input type="password" placeholder="Current password" value={form.old_password}
            onChange={(e) => setForm({ ...form, old_password: e.target.value })} data-testid="old-password-input" />
          <Input type="password" placeholder="New password (min 6 chars)" value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="new-password-input" />
          <Button onClick={submit} className="w-full bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="change-password-submit">Update Password</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();

  const quickActions = [
    { label: "New Invoice", to: "/invoices/new", module: "invoices", id: "quick-new-invoice" },
    { label: "New Booking", to: "/bookings?new=1", module: "bookings", id: "quick-new-booking" },
    { label: "New Quotation", to: "/quotations?new=1", module: "quotations", id: "quick-new-quotation" },
    { label: "Add Payment", to: "/payments?new=1", module: "payments", id: "quick-add-payment" },
    { label: "Add Expense", to: "/expenses?new=1", module: "expenses", id: "quick-add-expense" },
  ].filter((a) => can(user?.role, a.module, true));

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-40">
        <SidebarContent />
      </aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 shadow-2xl">
            <button className="absolute top-4 right-3 text-white z-10" onClick={() => setMobileOpen(false)}>
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#E5E0D8] px-4 sm:px-6 py-2.5 flex items-center gap-3">
          <button className="lg:hidden p-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5 text-[#0D4F4A]" />
          </button>
          <GlobalSearch />
          <div className="flex-1" />
          {quickActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="bg-[#C9A227] hover:bg-[#b8931f] text-[#092B28] font-semibold" data-testid="quick-actions-button">
                  <Plus className="h-4 w-4 mr-1" /> New <ChevronDown className="h-3.5 w-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {quickActions.map((a) => (
                  <DropdownMenuItem key={a.to} onSelect={() => navigate(a.to)} data-testid={a.id}>{a.label}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <NotificationsBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-[#FAF8F5] transition-colors" data-testid="user-menu">
                <span className="h-8 w-8 rounded-full bg-[#0D4F4A] text-[#D4AF37] flex items-center justify-center text-sm font-bold">
                  {(user?.name || "U")[0].toUpperCase()}
                </span>
                <span className="hidden sm:block text-left">
                  <span className="block text-xs font-semibold leading-tight">{user?.name}</span>
                  <span className="block text-[10px] text-[#5C6B69] leading-tight">{ROLE_LABELS[user?.role] || user?.role}</span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-[#5C6B69]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold">{user?.name}</div>
                <div className="text-xs text-[#5C6B69] font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setPwOpen(true)} data-testid="change-password-menu">
                <KeyRound className="h-4 w-4 mr-2" /> Change Password
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={logout} className="text-red-600" data-testid="logout-menu">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 max-w-[1500px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
